import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Card from "src/components/app/Card/Card";
import Header from "src/components/app/layout/Header";
import Layout from "src/components/app/layout/Layout";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { GetLabTestCart } from "src/types/labTests.types";
import TestDetailsInCart from "src/components/app/lab-tests/TestDetailsInCart";
import BillingView from "src/components/app/lab-tests/BillingView";
import { Calendar, Clock, Mail, MapPin, Phone } from "lucide-react";
import { formatDateToLocaleStringWithWeekDay } from "src/helpers/common";
import moment from "moment";
import {
  bookTestRequestAction,
  confirmTestRequestAction,
} from "src/redux/slices/labTestSlice";
import { razorpayResponse } from "src/types/razorPay.types";
import { toast } from "sonner";
import { sendMessageToParent } from "src/helpers/common";
import { POST_MESSAGE_EVENTS } from "src/constants/common";
import * as walletActions from "src/redux/slices/walletSlice";
import { calculateFinalAmountAfterWallet } from "src/helpers/walletHelpers";
import {
  initiateRazorpayPayment,
  RazorPayInitiateConfig,
} from "src/helpers/razorPayHelper";
import ProceedPaymentBottomSheet from "src/components/app/bottom-sheet/ProceedPaymentBottomSheet";
import environment from "src/constants/environment";

export const TestSummary: React.FC<{
  patientMemberName: string;
  address: string;
  email: string;
  phone: string;
}> = ({ patientMemberName, address, email, phone }) => {
  return (
    <div>
      <h2 className="text-md font-semibold mb-2">Summary</h2>
      <Card title={`${patientMemberName}`} stackTitleAndDescription={true}>
        <div className="space-y-4 pt-2">
          <div className="flex items-center gap-2">
            <div className="pt-1">
              <MapPin className="w-5 h-5 flex-shrink-0" />
            </div>
            <p className="text-sm text-gray-500"> {address}</p>
          </div>
          {email && (
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <p className="text-sm text-gray-500">{email}</p>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4" />
            <p className="text-sm text-gray-500">+91 {phone}</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export const SampleCollectionDetails: React.FC<{
  date: Date | string | null;
  timeSlot: string | null;
}> = ({ date, timeSlot }) => {
  return (
    <div>
      <h2 className="text-md font-semibold mb-2">Sample Collection Details</h2>
      <Card stackTitleAndDescription={true}>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <p className="text-sm text-gray-500">
              {" "}
              {formatDateToLocaleStringWithWeekDay(date)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <p className="text-sm text-gray-500">{timeSlot}</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

const LabTestSummaryScreen: React.FC = () => {
  const cartDetails: GetLabTestCart | null = useAppSelector(
    (state) => state.labTest.cartState.data
  );
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const navigate = useNavigate();
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const locationContext = useLocation();
  const selectedDate = locationContext.state?.selectedDate;
  const selectedTimeSlot = locationContext.state?.selectedTimeSlot;
  const selectedTimeSlotId = locationContext.state?.selectedTimeSlotId;
  const patientMemberDetails = locationContext.state?.patient;
  const address = locationContext.state?.address;
  const patientDetails = useAppSelector((state) => state.auth.patient.data);
  const bookTestState = useAppSelector((state) => state.labTest.bookTest);
  const confirmTestState = useAppSelector((state) => state.labTest.confirmTest);
  const currentUsedWalletMoney = useAppSelector(
    (state) => state.wallet.currentUsedWalletMoney
  );
  const [isBookTestLoading, setIsSetBookTestLoading] = useState<boolean>(false);
  const [isPaymentOrder, setIsPaymentOrder] = useState<boolean>(false);
  const discountData = useAppSelector(
    (state) => state.offers.CoupanDiscount.data
  );
  const [finalAmountToPay, setFinalAmountToPay] = useState<number>(0);

  const dispatch = useAppDispatch();
  useEffect(() => {
    if (isBookTestLoading && bookTestState.isSuccess) {
      setIsSetBookTestLoading(false);
      if (cartDetails?.tests_list.every((a) => a.type === "bcp") === true) {
        setIsPaymentOrder(true);
      } else {
        setIsPaymentOrder(true);
        razorPayPayment(bookTestState.data?.razorpay_order_id || null);
      }
    } else if (isBookTestLoading && bookTestState.isSuccess == false) {
      setIsSetBookTestLoading(false);
    }
    if (isPaymentOrder && confirmTestState.isSuccess == true) {
      setIsPaymentOrder(false);
    } else if (isPaymentOrder && confirmTestState.isSuccess == false) {
      setIsPaymentOrder(false);
    }
  }, [bookTestState.isSuccess, bookTestState.data]);

  useEffect(() => {
    const finalAmountAfterDiscount =
      Number(cartDetails?.order_detail?.final_payable_amount) -
      Number(discountData?.discount_amount || 0);

    const finalAmount = calculateFinalAmountAfterWallet(
      finalAmountAfterDiscount,
      currentUsedWalletMoney
    );

    setFinalAmountToPay(finalAmount);
  }, [cartDetails, discountData, currentUsedWalletMoney]);

  if (!cartDetails) {
    return <div>No cart details found</div>;
  }

  const razorPayPayment = async (orderId: string | null) => {
    // Validate order ID
    if (!orderId) {
      console.error("Invalid order ID");
      return;
    }
    if (orderId === " ") {
      confirmOrder(null);
      return;
    }
    const options: RazorPayInitiateConfig = {
      amount: Number(finalAmountToPay),
      order_id: orderId,
      name: "GoodFlip",
      description: "Lab Test Payment",
      features: {
        wallet_on_checkout: currentUsedWalletMoney > 0 ? true : false,
      },
      prefill: {
        name: patientDetails?.name,
        email: patientDetails?.email,
        contact: patientDetails?.contact_no,
      },
      onSuccess: (response: any) => {
        dispatch(walletActions.setCurrentUsedWalletMoney(0));
        confirmOrder(response);
      },
      onFailure: (error: any) => {
        console.error("Payment failed", error);
        toast.error(error?.message || "Payment failed");
        setIsSetBookTestLoading(false);
      },
      onDismiss: () => {
        console.error("Payment dismissed");
        toast.error("Payment dismissed");
      },
    };

    await initiateRazorpayPayment(options);
  };

  const handleProceedToPayment = () => {
    if (!termsAccepted) return;
    setIsSetBookTestLoading(true);
    const { final_payable_amount } = cartDetails.order_detail;
    const final_amount_to_pay = final_payable_amount ?? 0;

    const payload = {
      contact_no: patientMemberDetails?.phone || patientDetails?.contact_no,
      address_id: address?.patient_address_rel_id,
      appointment_date: moment(selectedDate).format("YYYY-MM-DD"),
      collection_slot_id: selectedTimeSlotId,
      slot_time: selectedTimeSlot,
      transaction_id: "",
      vendor_flag: cartDetails?.vendor_flag,
      home_collection_charge: Number(
        cartDetails?.home_collection_charge?.payable_ammount ??
          cartDetails?.home_collection_charge?.ammount ??
          0
      ).toFixed(2), //TODO:
      //final_payable_amount: Number(final_amount_to_pay ?? 0).toFixed(2),
      final_payable_amount: finalAmountToPay.toFixed(2),
      // route?.params?.data?.billingDetails?.payable_amount ?? 0,
      order_total: Number(cartDetails?.order_detail?.order_total ?? 0).toFixed(
        2
      ),
      payable_amount: Number(
        cartDetails?.order_detail?.payable_amount ?? 0
      ).toFixed(2),
      service_charge: Number(
        cartDetails?.order_detail?.service_charge ?? 0
      ).toFixed(2),
    };
    if (environment.ENV !== "prod") {
      Object.assign(payload, {
        dev: true,
      });
    }
    if (cartDetails?.tests_list.every((a) => a.type === "bcp") === true) {
      Object.assign(payload, {
        bcp_flag: "Y",
        final_payable_amount: 0,
        patient_plan_rel_id:
          cartDetails?.bcp_tests_list?.[0]?.patient_plan_rel_id,
        bcp_test_price_data: {
          bcp_final_amount_to_pay: "0",
          bcp_home_collection_charge: "0",
          bcp_home_collection_charge_old: "0",
          bcp_service_charge: "0",
          bcp_total_amount: "0",
          bcp_total_amount_old: "0",
        },
      });
    }
    if (cartDetails?.tests_list.some((a) => a.type === "bcp") === true) {
      Object.assign(payload, {
        bcp_flag: "Y",
        final_payable_amount: Number(final_payable_amount).toFixed(2),
        // route?.params?.data?.billingDetails?.payable_amount,
        patient_plan_rel_id:
          cartDetails?.bcp_tests_list?.[0]?.patient_plan_rel_id,
        bcp_test_price_data: {
          bcp_final_amount_to_pay: Number(
            cartDetails?.order_detail?.payable_amount
          ).toFixed(2),
          bcp_home_collection_charge:
            Number(
              cartDetails?.home_collection_charge?.payable_ammount ?? 0
            ).toFixed(2) ?? 0,
          bcp_home_collection_charge_old: Number(
            cartDetails?.home_collection_charge?.payable_ammount ?? 0
          ).toFixed(2),
          bcp_service_charge: Number(
            cartDetails?.order_detail?.service_charge ?? 0
          ).toFixed(2),
          bcp_total_amount: Number(
            cartDetails?.order_detail?.payable_amount ?? 0
          ).toFixed(2),
          bcp_total_amount_old: Number(
            cartDetails?.order_detail?.payable_amount ?? 0
          ).toFixed(2),
        },
      });
    }
    if (!cartDetails?.tests_list.some((a) => a.type === "bcp")) {
      Object.assign(payload, {
        member_id: patientMemberDetails?.patient_member_rel_id,
      });
    }
    if (discountData?.discount_amount) {
      Object.assign(payload, {
        discount_amount: discountData?.discount_amount,
        discount_type: discountData?.discount_code?.discount_type,
        discounts_master_id: discountData?.discount_code?.discounts_master_id,
      });
    }
    if (
      cartDetails?.order_detail?.wallet_discount_applied ||
      currentUsedWalletMoney > 0
    ) {
      // Use the currentUsedWalletMoney directly as it's already calculated with minimum payment logic
      const walletAmount = currentUsedWalletMoney;
      Object.assign(payload, {
        wallet_amount: walletAmount * 100,
        wallet_payment: "Y",
      });
    }

    new Promise((resolve, reject) => {
      dispatch(bookTestRequestAction({ resolve, reject, payload }));
    })
      .then((res: any) => {
        // Validate the response
        if (!res?.data?.razorpay_order_id) {
          console.error("No Razorpay order ID received from backend");
          setIsSetBookTestLoading(false);
          return;
        }
      })
      .catch((err) => {
        console.error("book test error", err);
        setIsSetBookTestLoading(false);
        toast.error("Something went wrong");
      });
  };

  const confirmOrder = (data: razorpayResponse | null) => {
    setIsSetBookTestLoading(true);
    const payload = {
      order_master_id: bookTestState.data?.order_master_id,
    };
    if (data != null) {
      Object.assign(payload, {
        transaction_id: data?.razorpay_payment_id,
      });
    }
    new Promise((resolve, reject) => {
      dispatch(confirmTestRequestAction({ resolve, reject, payload }));
    })
      .then((res: any) => {
        navigate(`/app/${supportProgramKey}/book-lab-test/congratulation`);
        toast.success("Lab test booked successfully");
      })
      .catch((err) => {
        console.error("confirm order error", err);
        toast.error("Something went wrong");
      })
      .finally(() => {
        setIsSetBookTestLoading(false);
      });
  };

  return (
    <Layout supportProgramKey={supportProgramKey || ""}>
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-10 max-w-lg mx-auto">
        <Header
          title="Summary"
          titlePosition="left"
          onBackClick={() => {
            //navigate(-1);
            navigate(`/app/${supportProgramKey}/book-lab-test/checkout`);
            dispatch(walletActions.setCurrentUsedWalletMoney(0));
          }}
        />
      </div>

      {/* Page Content */}
      <div className="pt-16 pb-40 px-4 space-y-4">
        <TestSummary
          patientMemberName={patientMemberDetails?.name}
          address={address?.address}
          email={patientMemberDetails?.email}
          phone={patientMemberDetails?.phone || patientDetails?.contact_no}
        />
        <SampleCollectionDetails
          date={selectedDate}
          timeSlot={selectedTimeSlot}
        />
        {/* Test Details */}
        <div>
          <h2 className="text-md font-semibold mb-2">Test Details</h2>
          <Card stackTitleAndDescription={true}>
            <TestDetailsInCart
              testListInCart={cartDetails?.tests_list}
              showAddMoreTest={false}
              disableDeleteTest={true}
              disableAddMoreTest={true}
            />
          </Card>
        </div>
        {/* Billing View */}
        <div>
          <h2 className="text-md font-semibold mb-2">Billing</h2>
          <Card stackTitleAndDescription={true}>
            <BillingView
              billingTitle="Item Total"
              discountTitle="Discount on Item(s)"
              orderTotal={cartDetails?.order_detail?.order_total}
              homeCollectionCharge={
                cartDetails?.home_collection_charge?.payable_ammount ||
                cartDetails?.home_collection_charge?.ammount ||
                0
              }
              serviceCharge={cartDetails?.order_detail?.service_charge}
              finalPayableAmount={finalAmountToPay}
              discount={
                Number(cartDetails?.order_detail?.order_total) -
                Number(cartDetails?.order_detail?.payable_amount)
              }
              couponDiscount={Number(discountData?.discount_amount || 0)}
              usedWalletMoney={currentUsedWalletMoney}
            />
          </Card>
        </div>
        <div className="flex items-center space-x-2">
          <input
            id="terms"
            type="checkbox"
            className="w-4 h-4 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
          />
          <label
            htmlFor="terms"
            className="text-sm text-gray-500 cursor-pointer"
          >
            I agree to the{" "}
            <button
              type="button"
              className="text-blue-600 underline hover:text-blue-800 cursor-pointer"
              onClick={() => {
                const data = {
                  title: "Terms & Conditions",
                  link:
                    cartDetails?.labtest_tnc_link ||
                    "https://shop.goodflip.in/pages/terms-conditions", // Replace with your URL
                };
                sendMessageToParent(
                  POST_MESSAGE_EVENTS.OPEN_EXTERNAL_LINK,
                  data
                );
              }}
            >
              Terms & Conditions
            </button>
          </label>
        </div>
      </div>

      <ProceedPaymentBottomSheet
        finalAmountToPay={finalAmountToPay}
        handleProceedToPayment={handleProceedToPayment}
        termsAccepted={termsAccepted}
        isLoading={isBookTestLoading}
      />
    </Layout>
  );
};

export default LabTestSummaryScreen;
